import { useMemo } from 'react';
import useGetTourSlug, {
  getTourSlugQueryOptions,
} from '@/features/dashboard/tours/hooks/useGetTourSlug';
import useGetCompanySlug, {
  getCompanySlugQueryOptions,
} from '@/features/dashboard/company/hooks/useGetCompanySlug';
import CompanySlugDto from '@/features/dashboard/company/types/CompanySlugDto';
import TourSlugDto from '@/features/dashboard/tours/types/TourSlugDto';
import { toast } from 'sonner';
import { queryClient } from '@/App.tsx';
import TourDto from '@/features/dashboard/tourConfig/types/TourDto.ts';

export const buildTourLink = (
  companySlug: CompanySlugDto,
  tourSlug: TourSlugDto,
): string => {
  const origin = window.location.origin;
  return `${origin}/booking/${companySlug.slug}/${tourSlug.slug}`;
};

const useTourLink = (tourId: string) => {
  const { data: companySlug, isLoading: isLoadingCompany } =
    useGetCompanySlug();
  const { data: tourSlug, isLoading: isLoadingTour } = useGetTourSlug(tourId);

  const isLoading = isLoadingCompany || isLoadingTour;

  const tourLink = useMemo(() => {
    if (!companySlug || !tourSlug) {
      return '';
    }

    return buildTourLink(companySlug, tourSlug);
  }, [companySlug, tourSlug]);

  return { tourLink, isLoading };
};

export const copyTourLinkToClipboard = async (tourId: string) => {
  const loadingToast = toast.loading('Generowanie linku...');
  try {
    const [companySlug, tourSlug] = await Promise.all([
      queryClient.fetchQuery(getCompanySlugQueryOptions()),
      queryClient.fetchQuery(getTourSlugQueryOptions(tourId)),
    ]);

    const link = buildTourLink(companySlug, tourSlug);

    await navigator.clipboard.writeText(link);
    toast.success('Link skopiowany do schowka');
  } catch (error) {
    toast.error('Nie udało się wygenerować linku');
  } finally {
    toast.dismiss(loadingToast);
  }
};

export default useTourLink;
